import { useEffect, useState } from "react";
import { useProposalPdf } from '../../hooks/useProposalPdf';
import { useMutation, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Stack,
  Typography,
} from "@mui/joy";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { api } from "../../lib/api";
import { proposalsApi } from "../../lib/proposalsApi";
import type { ProposalDetail } from "../../types/proposals";

export interface AcceptanceWorkflow {
  state: "none" | "legacy" | "pending" | "processing" | "error" | "ready";
  pdf_url: string | null;
  order: null | {
    services?: import("../../shared/proposal-services").AcceptedService[];
    tier: null | {
      name: string;
      subtotal: number;
      tax_amount: number;
      total_amount: number;
    };
    upgrades: { id: number; name: string; total_price: number; is_required?: boolean }[];
    product_choices: { id: number; label: string }[];
    comments: string;
    total: string;
  };
}
export interface ProposalWorkflowData {
  acceptance: AcceptanceWorkflow;
  review_version: string;
  recipient: string;
  valid_until: string | null;
  effective_valid_until: string | null;
  pages: { id: number; title: string }[];
  preflight: { label: string; ok: boolean; detail?: unknown; tab?: string }[];
  events: {
    id: number;
    description: string;
    actor: string;
    created_at: string;
  }[];
  versions: { id: number; number: string; status: string }[];
}
const money = (value: string | number) =>
  Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" });
function validationMessages(detail: unknown): string[] {
  if (typeof detail === "string") return [detail];
  if (Array.isArray(detail)) return detail.flatMap(validationMessages);
  if (detail && typeof detail === "object") return Object.values(detail).flatMap(validationMessages);
  return [];
}
const errorText = (error: unknown) => {
  const value = error as {
    responseData?: { error?: string };
    message?: string;
  };
  return (
    value.responseData?.error ||
    value.message ||
    "Unable to complete the request. Please retry."
  );
};

export function AcceptedOrder({
  acceptance,
  customer = false,
}: {
  acceptance: AcceptanceWorkflow;
  customer?: boolean;
}) {
  const order = acceptance.order;
  return (
    <Stack spacing={1.5} sx={{ textAlign: "left" }}>
      {order && (
        <>
          <Typography level="title-lg">
            Accepted order · {order.tier?.name || "Project services"}
          </Typography>
          {order.tier && <Typography>
            Package subtotal {money(order.tier.subtotal)} · Tax{" "}
            {money(order.tier.tax_amount)}
          </Typography>}
          {order.services?.map(service => <Typography key={service.key}>{service.name} · {service.option.name} · {money(service.total)}</Typography>)}
          {order.upgrades.map((upgrade) => (
            <Typography key={upgrade.id}>
              {upgrade.name} · {money(upgrade.total_price)}
            </Typography>
          ))}
          <Typography level="title-lg">Total {money(order.total)}</Typography>
          {order.product_choices.map((choice) => (
            <Chip key={choice.id} variant="soft">
              {choice.label}
            </Chip>
          ))}
          {order.comments && (
            <Typography sx={{ whiteSpace: "pre-wrap" }}>
              Customer comments: {order.comments}
            </Typography>
          )}
        </>
      )}
      {acceptance.state === "legacy" && (
        <Alert color="neutral">
          This acceptance predates order snapshots. Contact the team for
          existing paperwork.
        </Alert>
      )}
      {["pending", "processing"].includes(acceptance.state) && (
        <Alert color="primary">
          Acceptance is saved. Your paperwork is being prepared; this page will
          update when it is ready.
        </Alert>
      )}
      {acceptance.state === "error" && (
        <Alert color="warning">
          Acceptance is saved. Paperwork is delayed.{" "}
          {customer
            ? "Our team can help recover your document."
            : "Retry document generation below."}
        </Alert>
      )}
      {customer && acceptance.pdf_url && (
        <Button component="a" href={acceptance.pdf_url}>
          Download accepted paperwork
        </Button>
      )}
    </Stack>
  );
}

export default function ProposalWorkflow({
  proposal,
  query,
  reviewOpen,
  onCloseReview,
}: {
  proposal: ProposalDetail;
  query: UseQueryResult<ProposalWorkflowData, Error>;
  reviewOpen: boolean;
  onCloseReview: () => void;
}) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const refresh = () => {
    client.invalidateQueries({ queryKey: ["proposal-workflow", proposal.id] });
    client.invalidateQueries({ queryKey: ["proposal", String(proposal.id)] });
    client.invalidateQueries({ queryKey: ["proposals"] });
  };
  const [recipientEmail, setRecipientEmail] = useState("");
  useEffect(() => {
    if (reviewOpen) setRecipientEmail(query.data?.recipient || "");
  }, [reviewOpen, proposal.id, query.data?.recipient]);
  const validRecipient = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    recipientEmail.trim(),
  );
  const send = useMutation({
    mutationFn: () =>
      proposalsApi.send(
        proposal.id,
        query.data!.review_version,
        recipientEmail.trim(),
      ),
    onSuccess: () => {
      refresh();
      onCloseReview();
      toast.success("Email invitation accepted for sending");
    },
    onError: (error) => {
      toast.error(errorText(error));
      query.refetch();
    },
  });
  const retry = useMutation({
    mutationFn: () =>
      api.post(`/api/proposals/${proposal.id}/retry-accepted-document/`, {}),
    onSuccess: refresh,
    onError: (error) => toast.error(errorText(error)),
  });
  const revise = useMutation({
    mutationFn: async () =>
      (
        await api.post<ProposalDetail>(
          `/api/proposals/${proposal.id}/revise/`,
          {},
        )
      ).data,
    onSuccess: (revision) => {
      refresh();
      navigate(`/crm/proposals/${revision.id}/edit`);
    },
    onError: (error) => toast.error(errorText(error)),
  });
  const pdf = useProposalPdf(proposal.id, proposal.proposal_number);
  if (query.isPending)
    return <Typography sx={{ mb: 3 }}>Loading proposal workflow…</Typography>;
  if (query.isError || !query.data)
    return (
      <Alert
        color="warning"
        sx={{ mb: 3 }}
        endDecorator={<Button onClick={() => query.refetch()}>Retry</Button>}
      >
        Workflow information is unavailable.
      </Alert>
    );
  const data = query.data;
  return (
    <>
      <Card sx={{ mb: 3 }}>
        <Typography level="title-lg">Documents and acceptance</Typography>
        {data.acceptance.state !== "none" && (
          <AcceptedOrder acceptance={data.acceptance} />
        )}
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Button
            component="a"
            href={`${proposal.public_url}?preview=1`}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
          >
            Customer preview
          </Button>
          <Button
            variant="outlined"
            loading={pdf.isPending}
            onClick={() => { void pdf.generate().catch(() => {}); }}
            disabled={["pending", "processing", "error"].includes(
              data.acceptance.state,
            )}
          >
            {!["none", "legacy"].includes(data.acceptance.state)
              ? "Accepted paperwork"
              : data.acceptance.state === "legacy"
                ? "Existing acceptance PDF"
                : "Proposal PDF"}
          </Button>
          {data.acceptance.state === "error" && (
            <Button loading={retry.isPending} onClick={() => retry.mutate()}>
              Retry paperwork
            </Button>
          )}
        </Stack>
        {pdf.message && (
          <Typography role="status" color={pdf.isError ? "danger" : "neutral"}>{pdf.message}</Typography>
        )}
        <Divider />
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Typography level="title-md">Version history</Typography>
          <Button
            variant="soft"
            loading={revise.isPending}
            onClick={() => revise.mutate()}
          >
            Create revision
          </Button>
        </Stack>
        <Typography level="body-sm">
          A revision starts a separate draft and preserves this proposal.
        </Typography>
        {data.versions.map((version) => (
          <Typography
            key={version.id}
            component={Link}
            to={`/crm/proposals/${version.id}`}
          >
            {version.number} · {version.status}
            {version.id === proposal.id ? " · Current" : ""}
          </Typography>
        ))}
      </Card>
      <Modal
        open={reviewOpen}
        onClose={send.isPending ? undefined : onCloseReview}
      >
        <ModalDialog
          sx={{ maxWidth: 560, width: "calc(100% - 32px)", overflow: "auto" }}
        >
          <Typography level="h4">Review before sending</Typography>
          <FormControl required>
            <FormLabel>Recipient email</FormLabel>
            <Input
              type="email"
              value={recipientEmail}
              disabled={send.isPending}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="name@example.com"
            />
          </FormControl>
          <Typography level="body-xs">
            Changes apply to this email only. The customer’s contact record
            stays the same.
          </Typography>
          <Typography>
            Valid through: {data.effective_valid_until || "No expiration"}
          </Typography>
          {data.effective_valid_until !== data.valid_until && (
            <Alert color="warning">
              Sending renews this expired proposal for 30 days.
            </Alert>
          )}
          <Typography level="body-sm">
            Reviewed version: {data.review_version.slice(0, 12)}
          </Typography>
          {data.preflight.map((check) => (
            <Stack key={check.label} spacing={1}>
            <Chip
              color={
                (check.label === "Customer email" ? validRecipient : check.ok)
                  ? "success"
                  : "danger"
              }
            >
              {(check.label === "Customer email" ? validRecipient : check.ok)
                ? "Ready"
                : "Needs attention"}{" "}
              · {check.label}
            </Chip>
            {!check.ok && check.label !== "Customer email" && (check.detail != null || check.tab === "services") && (
              <Alert color="danger">
                <Stack spacing={1}>
                  {validationMessages(check.detail).map((message, index) => <Typography key={index}>{message}</Typography>)}
                  {check.tab === "services" && <Button component={Link} to={`/crm/proposals/${proposal.id}/edit?tab=services`} variant="outlined" color="danger">Review service details</Button>}
                </Stack>
              </Alert>
            )}
            </Stack>
          ))}
          <Typography>
            Included pages:{" "}
            {data.pages.map((page) => page.title).join(", ") || "None"}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="plain"
              disabled={send.isPending}
              onClick={onCloseReview}
            >
              Cancel
            </Button>
            <Button
              loading={send.isPending}
              disabled={
                !validRecipient ||
                data.preflight.some(
                  (check) => check.label !== "Customer email" && !check.ok,
                ) ||
                query.isFetching
              }
              onClick={() => send.mutate()}
            >
              Send reviewed proposal
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </>
  );
}
